import React, { useState, useEffect } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { X, Folder as FolderIcon } from 'lucide-react';
import { useStore, Folder } from '../../stores';
import { generateId } from '../../lib/utils';

interface FolderDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  collectionId: string;
  editingFolder?: Folder | null;
}

interface FormData {
  name: string;
}

const emptyFormData = (): FormData => ({
  name: '',
});

export function FolderDialog({ open, onOpenChange, collectionId, editingFolder }: FolderDialogProps) {
  const { addFolder, updateFolder } = useStore();
  const [formData, setFormData] = useState<FormData>(emptyFormData());

  // Populate form when editing
  useEffect(() => {
    if (open && editingFolder) {
      setFormData({
        name: editingFolder.name,
      });
    } else if (!open) {
      setFormData(emptyFormData());
    }
  }, [open, editingFolder]);

  const isEditing = !!editingFolder;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      return;
    }

    if (isEditing && editingFolder) {
      updateFolder(collectionId, editingFolder.id, {
        name: formData.name.trim(),
      });
    } else {
      const newFolder: Folder = {
        id: generateId(),
        name: formData.name.trim(),
        requests: [],
      };
      addFolder(collectionId, newFolder);
    }

    onOpenChange(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && e.metaKey) {
      handleSubmit(e);
    }
  };

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/50 z-50" />
        <Dialog.Content
          className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-card border border-border rounded-lg shadow-xl z-50 w-[400px] max-h-[85vh] overflow-hidden"
          onKeyDown={handleKeyDown}
        >
          <div className="flex items-center justify-between px-6 py-4 border-b border-border">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-yellow-500/10 rounded-lg">
                <FolderIcon className="w-5 h-5 text-yellow-500" />
              </div>
              <Dialog.Title className="text-lg font-semibold">
                {isEditing ? 'Edit Folder' : 'New Folder'}
              </Dialog.Title>
            </div>
            <Dialog.Close asChild>
              <button className="p-1 rounded hover:bg-accent transition-colors">
                <X className="w-5 h-5 text-muted-foreground" />
              </button>
            </Dialog.Close>
          </div>

          <form onSubmit={handleSubmit} className="p-6 space-y-4">
            <div className="space-y-2">
              <label htmlFor="folder-name" className="text-sm font-medium text-foreground">
                Name <span className="text-red-500">*</span>
              </label>
              <input
                id="folder-name"
                type="text"
                value={formData.name}
                onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
                placeholder="My Folder"
                className="w-full px-3 py-2 bg-background border border-border rounded-md text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary"
                autoFocus
              />
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <button
                type="button"
                onClick={() => onOpenChange(false)}
                className="px-4 py-2 text-sm font-medium text-foreground bg-background border border-border rounded-md hover:bg-accent transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={!formData.name.trim()}
                className="px-4 py-2 text-sm font-medium text-primary-foreground bg-primary rounded-md hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isEditing ? 'Save Changes' : 'Create Folder'}
              </button>
            </div>
          </form>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
